<template>
    <div class="flex flex-col">
        <div class="flex justify-center items-center my-8" v-if="dateLabel">
            <hr class="grow border-gray-300" />
            <p
                data-testid="date-label"
                class="mx-4 my-0 text-sm font-semibold uppercase text-gray-500"
            >
                {{ dateLabel }}
            </p>
            <hr class="grow border-gray-300" />
        </div>

        <div data-testid="messages-container" class="flex flex-col gap-y-4 lg:px-4">
            <template :key="`message-${message.id || index}`" v-for="(message, index) in messages">
                <transition name="unread-separator">
                    <sms-unread-messages-separator
                        data-testid="inline-unread-separator"
                        ref="inlineUnreadSeparator"
                        v-if="index === unreadSeparatorIndex"
                    />
                </transition>
                <sms-conversation-message-list-item
                    :message="message"
                    :lead-name="leadName"
                    :show-time="shouldShowTimeSeparatorForMessage(message, index)"
                    :is-highlighted="messageIsHighlighted(message)"
                    :is-read-only="isReadOnly"
                    :is-retrying="retryingMessageIds.has(message.id)"
                    @highlight-animation-end="emit('highlight-animation-end')"
                    @retry="emit('retry', message.id)"
                />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch, nextTick, onBeforeUnmount, type PropType } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import { Message } from '@/entities/types/ConversationType.ts';
    import { adjustScrollAfterImages } from '@/components/sms/utils/adjustScrollAfterImages.ts';
    import SmsConversationMessageListItem from '@/components/sms/SmsConversationMessageListItem.vue';
    import SmsUnreadMessagesSeparator from '@/components/sms/SmsUnreadMessagesSeparator.vue';

    const MESSAGE_GROUP_GAP_MINUTES = 5;

    const props = defineProps({
        messages: {
            type: Array as PropType<Message[]>,
            required: true,
        },
        leadName: {
            type: String,
            required: true,
        },
        highlightedCommunicationId: {
            type: Number as PropType<number | null>,
            default: null,
        },
        highlightedMessageIds: {
            type: Set as unknown as PropType<Set<number>>,
            default: () => new Set<number>(),
        },
        firstUnreadMessageId: {
            type: Number as PropType<number | null>,
            default: null,
        },
        scrollRoot: {
            type: HTMLElement as unknown as PropType<HTMLElement | undefined>,
            default: undefined,
        },
        scrollToUnreadSeparatorRequestId: {
            type: Number as PropType<number>,
            default: 0,
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
        retryingMessageIds: {
            type: Set as unknown as PropType<Set<number>>,
            default: () => new Set<number>(),
        },
    });

    const emit = defineEmits<{
        (e: 'highlight-animation-end'): void;
        (e: 'unread-separator-visibility-change', visible: boolean): void;
        (e: 'retry', messageId: number): void;
    }>();

    const { t } = useI18n();

    const dateLabel = computed((): string => {
        if (!props.messages.length) {
            return '';
        }

        const activixDate = new ActivixDate(props.messages[0].createdAt);

        if (activixDate.isEmpty()) {
            return '';
        }

        const now = new ActivixDate('now');
        let label = activixDate.format('d MMM');

        if (activixDate.startOfDay().isSame(now.clone().startOfDay())) {
            label = t('smsCommunication.dateSeparator.today');
        } else if (activixDate.startOfDay().isSame(now.clone().subDays(1).startOfDay())) {
            label = t('smsCommunication.dateSeparator.yesterday');
        }

        return `${label} ${activixDate.toHumanTime()}`;
    });

    const isDifferentGroup = (message1: Message, message2: Message): boolean => {
        const first = new ActivixDate(message1.createdAt);
        const second = new ActivixDate(message2.createdAt);

        if (first.isEmpty() || second.isEmpty()) {
            return false;
        }

        return first.isBefore(second.subMinutes(MESSAGE_GROUP_GAP_MINUTES));
    };

    const shouldShowTimeSeparatorForMessage = (message: Message, index: number): boolean => {
        return index !== 0 && isDifferentGroup(props.messages[index - 1], message);
    };

    const messageIsHighlighted = (message: Message): boolean => {
        if (props.highlightedMessageIds.has(message.id)) {
            return true;
        }

        return (
            props.highlightedCommunicationId !== null &&
            message.communicationId === props.highlightedCommunicationId
        );
    };

    const inlineUnreadSeparator = ref<InstanceType<typeof SmsUnreadMessagesSeparator> | null>(null);

    const unreadSeparatorIndex = computed((): number | null => {
        if (!props.firstUnreadMessageId || !props.messages.length) {
            return null;
        }

        const index = props.messages.findIndex(
            (message) => message.id === props.firstUnreadMessageId
        );

        return index === -1 ? null : index;
    });

    const getInlineUnreadSeparatorElement = (): HTMLElement | undefined => {
        const component = Array.isArray(inlineUnreadSeparator.value)
            ? inlineUnreadSeparator.value[0]
            : inlineUnreadSeparator.value;

        return component?.$el as HTMLElement | undefined;
    };

    const scrollContainerToUnreadSeparator = (container: HTMLElement): void => {
        const element = getInlineUnreadSeparatorElement();

        if (!element) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const separatorRect = element.getBoundingClientRect();
        const separatorPosition = separatorRect.top - containerRect.top + container.scrollTop;

        container.scrollTop = Math.max(0, separatorPosition - 16);
    };

    watch(
        () => props.scrollToUnreadSeparatorRequestId,
        () => {
            if (unreadSeparatorIndex.value === null) {
                return;
            }

            nextTick(() => {
                if (!props.scrollRoot) {
                    return;
                }

                scrollContainerToUnreadSeparator(props.scrollRoot);
                adjustScrollAfterImages(props.scrollRoot, scrollContainerToUnreadSeparator);
            });
        }
    );

    let unreadSeparatorObserver: IntersectionObserver | null = null;

    const setupUnreadSeparatorObserver = (): void => {
        teardownUnreadSeparatorObserver();

        const element = getInlineUnreadSeparatorElement();

        if (!element) {
            return;
        }

        unreadSeparatorObserver = new IntersectionObserver(
            (entries) => {
                emit('unread-separator-visibility-change', entries[0].isIntersecting);
            },
            { root: props.scrollRoot, threshold: 0 }
        );

        unreadSeparatorObserver.observe(element);
    };

    const teardownUnreadSeparatorObserver = (): void => {
        unreadSeparatorObserver?.disconnect();
        unreadSeparatorObserver = null;
    };

    watch(
        unreadSeparatorIndex,
        (index) => {
            if (index === null) {
                teardownUnreadSeparatorObserver();
                return;
            }

            nextTick(setupUnreadSeparatorObserver);
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        teardownUnreadSeparatorObserver();
    });
</script>

<style scoped>
    .unread-separator-leave-active {
        @apply transition-all duration-300 overflow-hidden;
    }
    .unread-separator-leave-to {
        @apply opacity-0 max-h-0 !mt-0 !-mb-4;
    }
</style>
